import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  fetchSanitationEquipment,
  fetchSanitationRecords,
  createSanitationRecord,
  updateSanitationRecord,
  deleteSanitationRecord,
  createSanitationEquipment,
  deleteSanitationEquipment,
} from '../store/slices/sanitationSlice';
import DeleteEquipmentConfirmationModal from '../components/DeleteEquipmentConfirmationModal';
import { showToast } from '../utils/toastHelper';
import { fetchUsers } from '../store/slices/authSlice';
import { SanitationEquipment, SanitationRecord, CleaningFrequency } from '../types/sanitationRecord';
import { AuthUser } from '../types/auth';
import { matchesSearchQuery } from '../utils/searchUtils';

interface CollapsibleSectionProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, isExpanded, onToggle, children }) => {
  return (
    <div
      style={{
        border: '2px solid #ddd',
        borderRadius: '8px',
        marginBottom: '20px',
        backgroundColor: 'white',
        overflow: 'hidden',
      }}
    >
      <div
        onClick={onToggle}
        style={{
          padding: '16px',
          backgroundColor: '#f5f5f5',
          borderBottom: isExpanded ? '1px solid #ddd' : 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#eeeeee')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
      >
        <h3 style={{ margin: 0, fontSize: '1.2rem' }}>{title}</h3>
        <span
          style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#666',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'inline-block',
          }}
        >
          ▼
        </span>
      </div>
      {isExpanded && <div style={{ padding: '20px' }}>{children}</div>}
    </div>
  );
};

interface SanitationRecordTableProps {
  records: SanitationRecord[];
  equipmentId: string;
  users: AuthUser[];
  onAdd: (record: Partial<SanitationRecord>) => void;
  onUpdate: (id: string, record: Partial<SanitationRecord>) => void;
  onDelete: (id: string) => void;
}

const FREQUENCY_OPTIONS: CleaningFrequency[] = ['daily', 'weekly', 'monthly', 'quarterly'];

const SanitationRecordTable: React.FC<SanitationRecordTableProps> = ({
  records,
  equipmentId,
  users,
  onAdd,
  onUpdate,
  onDelete,
}) => {
  const [newRecord, setNewRecord] = useState<Partial<SanitationRecord>>({
    equipmentId,
    frequency: 'daily',
    cleaningDate: new Date().toISOString().split('T')[0],
    cleanedByUserId: null,
    verifiedByUserId: null,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRecord, setEditRecord] = useState<Partial<SanitationRecord>>({});
  const [searchTerm, setSearchTerm] = useState('');

  // Filter records based on search
  const filteredRecords = React.useMemo(() => {
    if (!searchTerm.trim()) return records;
    return records.filter(record => matchesSearchQuery(record, searchTerm, [
      record.cleanedByName || '',
      record.verifiedByName || ''
    ]));
  }, [records, searchTerm]);

  const handleAddRecord = () => {
    if (!newRecord.cleaningDate) return;
    onAdd(newRecord);
    setNewRecord({
      equipmentId,
      frequency: 'daily',
      cleaningDate: new Date().toISOString().split('T')[0],
      cleanedByUserId: null,
      verifiedByUserId: null,
    });
  };

  const handleStartEdit = (record: SanitationRecord) => {
    setEditingId(record.id);
    setEditRecord({
      equipmentId: record.equipmentId,
      frequency: record.frequency,
      cleaningDate: record.cleaningDate,
      cleanedByUserId: record.cleanedByUserId,
      verifiedByUserId: record.verifiedByUserId,
    });
  };

  const handleSaveEdit = () => {
    if (editingId && editRecord.cleaningDate) {
      onUpdate(editingId, editRecord);
      setEditingId(null);
      setEditRecord({});
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditRecord({});
  };

  const formatFrequency = (frequency: CleaningFrequency) => {
    return frequency.charAt(0).toUpperCase() + frequency.slice(1);
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '0.9rem',
    width: '100%',
    boxSizing: 'border-box',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    backgroundColor: 'white',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '6px 12px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    marginRight: '4px',
  };

  return (
    <div>
      {/* Search */}
      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search sanitation records..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 16px',
            fontSize: '14px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            outline: 'none',
            boxSizing: 'border-box' as const
          }}
        />
      </div>
      <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f8f9fa' }}>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: '15%' }}>
              Frequency
            </th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: '20%' }}>
              Cleaning Date
            </th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: '25%' }}>
              Cleaned By
            </th>
            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: '25%' }}>
              Verified By
            </th>
            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '15%' }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Add new record row */}
          <tr style={{ backgroundColor: '#f0fff4' }}>
            <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>
              <select
                value={newRecord.frequency || 'daily'}
                onChange={(e) => setNewRecord({ ...newRecord, frequency: e.target.value as CleaningFrequency })}
                style={selectStyle}
              >
                {FREQUENCY_OPTIONS.map((freq) => (
                  <option key={freq} value={freq}>
                    {formatFrequency(freq)}
                  </option>
                ))}
              </select>
            </td>
            <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>
              <input
                type="date"
                value={newRecord.cleaningDate || ''}
                onChange={(e) => setNewRecord({ ...newRecord, cleaningDate: e.target.value })}
                style={inputStyle}
              />
            </td>
            <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>
              <select
                value={newRecord.cleanedByUserId || ''}
                onChange={(e) => setNewRecord({ ...newRecord, cleanedByUserId: e.target.value || null })}
                style={selectStyle}
              >
                <option value="">Select user...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </td>
            <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>
              <select
                value={newRecord.verifiedByUserId || ''}
                onChange={(e) => setNewRecord({ ...newRecord, verifiedByUserId: e.target.value || null })}
                style={selectStyle}
              >
                <option value="">Select user...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </td>
            <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
              <button
                onClick={handleAddRecord}
                style={{
                  ...buttonStyle,
                  backgroundColor: '#4CAF50',
                  color: 'white',
                }}
              >
                Add
              </button>
            </td>
          </tr>

          {/* Data rows */}
          {filteredRecords.map((record) => (
            <tr key={record.id}>
              <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>
                {editingId === record.id ? (
                  <select
                    value={editRecord.frequency || 'daily'}
                    onChange={(e) => setEditRecord({ ...editRecord, frequency: e.target.value as CleaningFrequency })}
                    style={selectStyle}
                  >
                    {FREQUENCY_OPTIONS.map((freq) => (
                      <option key={freq} value={freq}>
                        {formatFrequency(freq)}
                      </option>
                    ))}
                  </select>
                ) : (
                  formatFrequency(record.frequency)
                )}
              </td>
              <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>
                {editingId === record.id ? (
                  <input
                    type="date"
                    value={editRecord.cleaningDate || ''}
                    onChange={(e) => setEditRecord({ ...editRecord, cleaningDate: e.target.value })}
                    style={inputStyle}
                  />
                ) : (
                  record.cleaningDate
                )}
              </td>
              <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>
                {editingId === record.id ? (
                  <select
                    value={editRecord.cleanedByUserId || ''}
                    onChange={(e) => setEditRecord({ ...editRecord, cleanedByUserId: e.target.value || null })}
                    style={selectStyle}
                  >
                    <option value="">Select user...</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  record.cleanedByName || '-'
                )}
              </td>
              <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}>
                {editingId === record.id ? (
                  <select
                    value={editRecord.verifiedByUserId || ''}
                    onChange={(e) => setEditRecord({ ...editRecord, verifiedByUserId: e.target.value || null })}
                    style={selectStyle}
                  >
                    <option value="">Select user...</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  record.verifiedByName || '-'
                )}
              </td>
              <td style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                {editingId === record.id ? (
                  <>
                    <button
                      onClick={handleSaveEdit}
                      style={{
                        ...buttonStyle,
                        backgroundColor: '#4CAF50',
                        color: 'white',
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      style={{
                        ...buttonStyle,
                        backgroundColor: '#6c757d',
                        color: 'white',
                      }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleStartEdit(record)}
                      style={{
                        ...buttonStyle,
                        backgroundColor: '#007bff',
                        color: 'white',
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDelete(record.id)}
                      style={{
                        ...buttonStyle,
                        backgroundColor: '#dc3545',
                        color: 'white',
                      }}
                    >
                      Delete
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}

          {records.length === 0 && (
            <tr>
              <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                No cleaning records yet. Add your first record above.
              </td>
            </tr>
          )}
          {records.length > 0 && filteredRecords.length === 0 && (
            <tr>
              <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                No records match your search for "{searchTerm}".
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    </div>
  );
};

export const SanitationRecords: React.FC = () => {
  const dispatch = useAppDispatch();
  const sanitationState = useAppSelector((state) => state.sanitation);
  const authState = useAppSelector((state) => state.auth);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [recordsFetched, setRecordsFetched] = useState<Record<string, boolean>>({});
  const [isAddEquipmentModalOpen, setIsAddEquipmentModalOpen] = useState(false);
  const [newEquipmentName, setNewEquipmentName] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [equipmentToDelete, setEquipmentToDelete] = useState<SanitationEquipment | null>(null);

  // Fetch equipment and users on mount
  useEffect(() => {
    dispatch(fetchSanitationEquipment());
    dispatch(fetchUsers());
  }, [dispatch]);

  const toggleSection = (equipmentId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [equipmentId]: !prev[equipmentId],
    }));

    // Fetch records when section is first expanded
    if (!expandedSections[equipmentId] && !recordsFetched[equipmentId]) {
      dispatch(fetchSanitationRecords(equipmentId));
      setRecordsFetched((prev) => ({ ...prev, [equipmentId]: true }));
    }
  };

  const getRecordsForEquipment = (equipmentId: string): SanitationRecord[] => {
    return sanitationState.records.filter((r) => r.equipmentId === equipmentId);
  };

  const handleAddRecord = (equipmentId: string, record: Partial<SanitationRecord>) => {
    dispatch(createSanitationRecord(record)).then(() => {
      // Refetch records to get user names from JOIN
      dispatch(fetchSanitationRecords(equipmentId));
    });
  };

  const handleUpdateRecord = (equipmentId: string, id: string, record: Partial<SanitationRecord>) => {
    dispatch(updateSanitationRecord({ id, record })).then(() => {
      dispatch(fetchSanitationRecords(equipmentId));
    });
  };

  const handleDeleteRecord = (id: string) => {
    dispatch(deleteSanitationRecord(id));
  };

  // Type-safe access to users array
  const users = (authState as { users?: AuthUser[] }).users || [];

  const handleAddEquipment = () => {
    if (!newEquipmentName.trim()) return;
    dispatch(createSanitationEquipment({ name: newEquipmentName.trim() }));
    setNewEquipmentName('');
    setIsAddEquipmentModalOpen(false);
  };

  const openDeleteModal = (equipment: SanitationEquipment, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent section toggle
    setEquipmentToDelete(equipment);
    setDeleteModalOpen(true);
  };

  const handleDeleteEquipment = async () => {
    if (!equipmentToDelete) return;
    try {
      await dispatch(deleteSanitationEquipment(equipmentToDelete.id)).unwrap();
      showToast(`Equipment "${equipmentToDelete.name}" deleted successfully`, 'success');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete equipment';
      showToast(errorMessage, 'error');
    } finally {
      setDeleteModalOpen(false);
      setEquipmentToDelete(null);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      {/* Sticky Header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10,
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <header style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ fontSize: '2rem', color: '#333', margin: '0 0 8px 0' }}>Sanitation Records</h1>
              <p style={{ color: '#666', fontSize: '1rem', margin: 0 }}>
                Track equipment cleaning and sanitation
              </p>
            </div>
            <button
              onClick={() => setIsAddEquipmentModalOpen(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '1rem',
                cursor: 'pointer',
              }}
            >
              Add Equipment
            </button>
          </header>
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px',
          backgroundColor: '#f8fafc',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {sanitationState.equipment.map((equipment: SanitationEquipment) => (
            <div key={equipment.id} style={{ position: 'relative' }}>
              <button
                onClick={(e) => openDeleteModal(equipment, e)}
                style={{
                  position: 'absolute',
                  top: '14px',
                  right: '48px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  opacity: 0.6,
                  transition: 'opacity 0.2s, color 0.2s',
                  zIndex: 1,
                  padding: '4px 8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.color = '#d32f2f';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.6';
                  e.currentTarget.style.color = 'inherit';
                }}
                title="Delete equipment"
              >
                🗑️
              </button>
              <CollapsibleSection
                title={equipment.name}
                isExpanded={expandedSections[equipment.id] || false}
                onToggle={() => toggleSection(equipment.id)}
              >
                <SanitationRecordTable
                  records={getRecordsForEquipment(equipment.id)}
                  equipmentId={equipment.id}
                  users={users}
                  onAdd={(record) => handleAddRecord(equipment.id, record)}
                  onUpdate={(id, record) => handleUpdateRecord(equipment.id, id, record)}
                  onDelete={handleDeleteRecord}
                />
              </CollapsibleSection>
            </div>
          ))}

          {sanitationState.equipment.length === 0 && !sanitationState.loading && (
            <div
              style={{
                textAlign: 'center',
                padding: '40px',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '2px solid #ddd',
              }}
            >
              <p style={{ color: '#666', fontSize: '1.1rem' }}>
                No equipment found. Run the database migration to seed default equipment.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Add Equipment Modal */}
      {isAddEquipmentModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setIsAddEquipmentModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              width: '400px',
              maxWidth: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 20px 0', fontSize: '1.5rem', color: '#333' }}>
              Add Equipment
            </h2>
            <div style={{ marginBottom: '20px' }}>
              <label
                htmlFor="equipmentName"
                style={{ display: 'block', marginBottom: '8px', fontWeight: 500, color: '#333' }}
              >
                Equipment Name
              </label>
              <input
                id="equipmentName"
                type="text"
                value={newEquipmentName}
                onChange={(e) => setNewEquipmentName(e.target.value)}
                placeholder="Enter equipment name"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                }}
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => {
                  setIsAddEquipmentModalOpen(false);
                  setNewEquipmentName('');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddEquipment}
                disabled={!newEquipmentName.trim()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: newEquipmentName.trim() ? '#4CAF50' : '#a5d6a7',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  cursor: newEquipmentName.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Equipment Confirmation Modal */}
      <DeleteEquipmentConfirmationModal
        isOpen={deleteModalOpen}
        equipmentName={equipmentToDelete?.name || ''}
        onConfirm={handleDeleteEquipment}
        onCancel={() => {
          setDeleteModalOpen(false);
          setEquipmentToDelete(null);
        }}
      />
    </div>
  );
};
